import React, { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  Clock,
  Film,
  MapPin,
  Users,
  Calendar,
  Ticket,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Trash2,
  Printer,
  Download,
  Star,
  AlertCircle,
  Share2,
  RefreshCw,
  QrCode,
} from "lucide-react";

export default function MyBooking() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [filter, setFilter] = useState("all"); // 'all', 'upcoming', 'past'
  const [sortBy, setSortBy] = useState("date-desc"); // 'date-desc', 'date-asc', 'name-asc', 'name-desc'
  const [cancellingBookingId, setCancellingBookingId] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("success"); // 'success', 'error', 'info'

  // Define pdfRef as a ref object that will hold booking content
  const pdfRef = useRef(null);

  useEffect(() => {
    const loadBookings = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Authentication token missing.");

        const { userId } = jwtDecode(token);
        const { data } = await axios.get(
          `https://apigrand.dodunsoftsolutions.com/api/booking/mybooking/${userId}`
        );

        if (data.success) {
          setBookings(data.data);

          // Auto-expand the first booking if there's only one
          if (data.data.length === 1) {
            setExpandedBooking(data.data[0]._id);
          }
        } else {
          throw new Error("Failed to load bookings.");
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Something went wrong.");
      } finally {
        setLoading(false);
      }
    };

    loadBookings();
  }, []);

  const toggleExpand = (id) => {
    if (expandedBooking === id) {
      setExpandedBooking(null);
    } else {
      setExpandedBooking(id);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const formatSimpleDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isUpcoming = (showTime) => {
    return new Date(showTime) > new Date();
  };

  const getDayDifference = (showTime) => {
    const now = new Date();
    const show = new Date(showTime);
    const diffTime = Math.abs(show - now);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (show > now) {
      return diffDays === 0
        ? "Today"
        : diffDays === 1
        ? "Tomorrow"
        : `In ${diffDays} days`;
    } else {
      return diffDays === 0
        ? "Today"
        : diffDays === 1
        ? "Yesterday"
        : `${diffDays} days ago`;
    }
  };

  const handleDownloadPDF = async (bookingId) => {
    const element = document.getElementById(`booking-${bookingId}`);
    if (!element) {
      console.error("Element not found");
      return;
    }

    // Show notification
    setNotificationType("info");
    setNotificationMessage("Generating your ticket PDF...");
    setShowNotification(true);

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: element.offsetWidth,
        height: element.offsetHeight,
      });

      const imgData = canvas.toDataURL("image/png");

      // Create PDF with proper dimensions
      const pdfWidth = 595.28; // A4 width in pts
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: [pdfWidth, pdfHeight],
      });

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`movie-ticket-${bookingId}.pdf`);

      // Show success notification
      setNotificationType("success");
      setNotificationMessage("Ticket downloaded successfully!");
    } catch (err) {
      console.error("PDF generation error:", err);
      setNotificationType("error");
      setNotificationMessage("Failed to generate ticket. Please try again.");
    }

    // Hide notification after 3 seconds
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  const handleDownloadTicket = (booking) => {
    // Use the PDF generation function
    handleDownloadPDF(booking._id);
  };

  const handlePrintTicket = (booking) => {
    // In a real app, this would open a print dialog with ticket details
    setNotificationType("info");
    setNotificationMessage("Preparing ticket for printing...");
    setShowNotification(true);

    setTimeout(() => {
      setShowNotification(false);
      window.print();
    }, 1000);
  };

  const shareBooking = (booking) => {
    const { movieId, showTime } = booking.showId;
    const text = `I'm watching ${movieId.movieName} on ${formatSimpleDate(
      showTime
    )} at ${formatTime(showTime)}!`;

    if (navigator.share) {
      navigator
        .share({
          title: `My Booking: ${movieId.movieName}`,
          text: text,
          // url: 'https://yourmoviesite.com/booking/' + booking._id
        })
        .catch(console.error);
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setNotificationType("success");
          setNotificationMessage("Booking details copied to clipboard!");
          setShowNotification(true);

          setTimeout(() => {
            setShowNotification(false);
          }, 3000);
        })
        .catch(console.error);
    }
  };

  const refreshBookings = () => {
    setLoading(true);
    setError(null);

    // Re-trigger the loadBookings effect
    const loadBookings = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Authentication token missing.");

        const { userId } = jwtDecode(token);
        const { data } = await axios.get(
          `https://apigrand.dodunsoftsolutions.com/api/booking/mybooking/${userId}`
        );

        if (data.success) {
          setBookings(data.data);
        } else {
          throw new Error("Failed to load bookings.");
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Something went wrong.");
      } finally {
        setLoading(false);
      }
    };

    loadBookings();
  };

  const filteredBookings = bookings.filter((booking) => {
    if (filter === "all") return true;
    if (filter === "upcoming") return isUpcoming(booking.showId.showTime);
    if (filter === "past") return !isUpcoming(booking.showId.showTime);
    return true;
  });

  const sortedBookings = [...filteredBookings].sort((a, b) => {
    switch (sortBy) {
      case "date-asc":
        return new Date(a.showId.showTime) - new Date(b.showId.showTime);
      case "date-desc":
        return new Date(b.showId.showTime) - new Date(a.showId.showTime);
      case "name-asc":
        return a.showId.movieId.movieName.localeCompare(
          b.showId.movieId.movieName
        );
      case "name-desc":
        return b.showId.movieId.movieName.localeCompare(
          a.showId.movieId.movieName
        );
      default:
        return new Date(b.showId.showTime) - new Date(a.showId.showTime);
    }
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-16 h-16 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
        <span className="mt-4 text-gray-500">Loading your bookings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-3xl mx-auto bg-red-50 border border-red-200 rounded-lg">
        <h2 className="text-lg font-semibold text-red-700 mb-2">
          Something went wrong
        </h2>
        <p className="text-red-600">{error}</p>
        <button
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="p-6 max-w-3xl mx-auto text-center space-y-6">
        <div className="text-gray-400">
          <Ticket className="w-16 h-16 mx-auto" />
        </div>
        <h2 className="text-xl font-semibold text-gray-700">
          No Bookings Found
        </h2>
        <p className="text-gray-600">
          You haven't made any bookings yet. Browse our movies and book your
          next show!
        </p>
        <button
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          onClick={() => (window.location.href = "/movies")}
        >
          Browse Movies
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Notification Toast */}
      {showNotification && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 max-w-md animate-slide-in-right ${
            notificationType === "success"
              ? "bg-green-100 text-green-800 border border-green-200"
              : notificationType === "error"
              ? "bg-red-100 text-red-800 border border-red-200"
              : "bg-blue-100 text-blue-800 border border-blue-200"
          }`}
        >
          {notificationType === "success" && <Star className="w-5 h-5" />}
          {notificationType === "error" && <AlertCircle className="w-5 h-5" />}
          {notificationType === "info" && <Ticket className="w-5 h-5" />}
          <p>{notificationMessage}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-800">My Bookings</h1>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={refreshBookings}
            className="px-3 py-2 border rounded-md bg-white hover:bg-gray-50 flex items-center gap-1"
            title="Refresh bookings"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden md:inline">Refresh</span>
          </button>

          <select
            className="px-3 py-2 border rounded-md bg-white"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Bookings</option>
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
          </select>

          <select
            className="px-3 py-2 border rounded-md bg-white"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="name-asc">Movie A-Z</option>
            <option value="name-desc">Movie Z-A</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {sortedBookings.map((b) => {
          const {
            _id,
            showId: { movieId, audiId, showTime },
            bookedSeats,
            finalAmount,
            paymentStatus,
            createdAt,
            baseAmount,
            gstAmount,
            voucherDiscount,
            walletPointsUsed,
            paymentMethod,
            customerName,
            customerPhone,
          } = b;

          const isExpanded = expandedBooking === _id;
          const showDate = new Date(showTime);
          const isPast = showDate < new Date();
          const canCancel = !isPast && paymentStatus !== "Cancelled";

          // Calculate time remaining before the show
          const timeUntilShow = getDayDifference(showTime);

          return (
            <div
              key={_id}
              className={`border rounded-lg overflow-hidden transition-all duration-300 ${
                isExpanded ? "shadow-md" : "shadow-sm"
              } ${
                paymentStatus === "Cancelled"
                  ? "bg-gray-50 border-gray-200"
                  : isPast
                  ? "bg-gray-50 border-gray-200"
                  : "bg-white border-blue-200"
              }`}
            >
              <div
                className={`px-4 py-3 flex justify-between items-center cursor-pointer ${
                  paymentStatus === "Cancelled"
                    ? "bg-gray-100"
                    : isPast
                    ? "bg-gray-100"
                    : "bg-blue-50"
                }`}
                onClick={() => toggleExpand(_id)}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-12 rounded-sm ${
                      paymentStatus === "Cancelled"
                        ? "bg-red-400"
                        : isPast
                        ? "bg-gray-400"
                        : "bg-green-500"
                    }`}
                  ></div>
                  <div className="flex items-center gap-2">
                    {movieId.image && (
                      <div className="hidden md:block w-10 h-14 rounded overflow-hidden">
                        <img
                          src={movieId.image}
                          alt={movieId.movieName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-lg">
                        {movieId.movieName}
                      </h3>
                      <div className="text-sm text-gray-600 flex flex-wrap items-center gap-2">
                        <span>{formatSimpleDate(showTime)}</span>
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                          {timeUntilShow}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-semibold">₹{baseAmount}</div>
                    <div
                      className={`text-sm ${
                        paymentStatus === "Confirmed"
                          ? "text-green-600"
                          : paymentStatus === "Pending"
                          ? "text-yellow-600"
                          : paymentStatus === "Cancelled"
                          ? "text-red-600"
                          : "text-gray-600"
                      }`}
                    >
                      {paymentStatus}
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </div>

              {isExpanded && (
                <>
                  <div
                    id={`booking-${_id}`}
                    className="p-4 space-y-4 border-t bg-white print:bg-white"
                  >
                    {/* Ticket header */}
                    <div className="flex justify-between items-center border-b pb-3 mb-3">
                      <h2 className="font-bold text-xl text-blue-600">
                        Movie Ticket
                      </h2>
                      <div className="text-sm text-gray-500">
                        Booking ID: {_id.slice(-8)}
                      </div>
                    </div>

                    <div className="md:flex items-start gap-6">
                      {movieId.image && (
                        <div className="hidden md:block w-32 h-48 rounded overflow-hidden shadow mb-4 md:mb-0 flex-shrink-0">
                          <img
                            src={movieId.image}
                            alt={movieId.movieName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      <div className="flex-grow">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <div className="flex items-start gap-2">
                              <Film className="w-5 h-5 mt-0.5 text-gray-500" />
                              <div>
                                <div className="font-medium">Movie</div>
                                <div>{movieId.movieName}</div>
                                <div className="text-sm text-gray-600">
                                  {movieId.language} • {movieId.certificate}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-start gap-2">
                              <Clock className="w-5 h-5 mt-0.5 text-gray-500" />
                              <div>
                                <div className="font-medium">Show Time</div>
                                <div>{formatDate(showTime)}</div>
                                <div className="text-sm text-blue-600 font-medium">
                                  {timeUntilShow}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-start gap-2">
                              <MapPin className="w-5 h-5 mt-0.5 text-gray-500" />
                              <div>
                                <div className="font-medium">Auditorium</div>
                                <div>{audiId.audiName}</div>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-start gap-2">
                              <Users className="w-5 h-5 mt-0.5 text-gray-500" />
                              <div>
                                <div className="font-medium">
                                  Seats ({bookedSeats.length})
                                </div>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {bookedSeats.map((s, idx) => (
                                    <span
                                      key={idx}
                                      className={`px-2 py-1 text-sm rounded ${
                                        s.type === "Recliner"
                                          ? "bg-purple-100 text-purple-800"
                                          : s.type === "Premium"
                                          ? "bg-blue-100 text-blue-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                                    >
                                      {s.row}
                                      {s.seatNumber}
                                      <span className="text-xs ml-1">
                                        ({s.type})
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-start gap-2">
                              <Calendar className="w-5 h-5 mt-0.5 text-gray-500" />
                              <div>
                                <div className="font-medium">Booking Date</div>
                                <div>{formatSimpleDate(createdAt)}</div>
                              </div>
                            </div>

                            <div className="flex items-start gap-2">
                              <CreditCard className="w-5 h-5 mt-0.5 text-gray-500" />
                              <div>
                                <div className="font-medium">
                                  Payment Method
                                </div>
                                <div className="capitalize">
                                  {paymentMethod}
                                </div>
                                <div
                                  className={`text-sm ${
                                    paymentStatus === "Confirmed"
                                      ? "text-green-600"
                                      : paymentStatus === "Pending"
                                      ? "text-yellow-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {paymentStatus}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Customer & Pricing Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Customer Details */}
                      <div className="space-y-2">
                        <h3 className="font-medium text-gray-700">
                          Customer Details
                        </h3>
                        <div className="p-3 bg-gray-50 rounded border">
                          <div className="text-sm">
                            <div className="grid grid-cols-3 gap-1 mb-1">
                              <span className="text-gray-500">Name:</span>
                              <span className="col-span-2">{customerName}</span>
                            </div>
                            <div className="grid grid-cols-3 gap-1">
                              <span className="text-gray-500">Contact:</span>
                              <span className="col-span-2">
                                {customerPhone}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Price Details */}
                      <div className="space-y-2">
                        <h3 className="font-medium text-gray-700">
                          Price Details
                        </h3>
                        <div className="p-3 bg-gray-50 rounded border">
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Base Amount</span>
                              <span>₹{baseAmount.toFixed(2)}</span>
                            </div>

                            {voucherDiscount > 0 && (
                              <div className="flex justify-between text-green-600">
                                <span>Voucher Discount</span>
                                <span>-₹{voucherDiscount.toFixed(2)}</span>
                              </div>
                            )}
                            {walletPointsUsed > 0 && (
                              <div className="flex justify-between text-green-600">
                                <span>Wallet Points Used</span>
                                <span>-₹{walletPointsUsed.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-semibold pt-2 border-t mt-2">
                              <span>Total Amount</span>
                              <span>₹{baseAmount}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer for ticket */}
                    <div className="text-center text-sm text-gray-500 border-t pt-3 mt-3">
                      <p>
                        This ticket is valid only for the specified show. Please
                        arrive at least 15 minutes before showtime.
                      </p>
                    </div>
                  </div>

                  {/* Action buttons - placed INSIDE the expanded area */}
                  <div className="px-4 py-2 border-t bg-gray-50 flex justify-end gap-2">
                   

                  

                    <button
                      onClick={() => handlePrintTicket(b)}
                      className="flex items-center gap-1 px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                      disabled={paymentStatus === "Cancelled"}
                    >
                      <Printer className="w-4 h-4" />
                      Print
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary at the bottom */}

      {/* Add custom styles for animations and print styling */}
      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease forwards;
        }

        @media print {
          body * {
            visibility: hidden;
          }

          #booking-${expandedBooking} * {
            visibility: visible;
          }

          #booking-${expandedBooking} {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
